import React, { useEffect, useState } from 'react';
import CopyBox from '../CopyBox';
import CreateApiKeyButton from './CreateApiKeyButton';
import { DeleteApiKeyButton } from './DeleteApiKeyButton';
import { CreateApiKeyFunction, DeleteApiKeyFunction, DevSetupTabProps } from './DevSetupTab';
import InlineNotification from "../Notification"
import { ApiKeyRequestStatus } from '../../../dashboard/saas/types';
import TemporaryContent from '../TemporaryContent';
import { CustomEventNames } from '../../../constants';

type ApiKeyListProps = Pick<DevSetupTabProps, "apiKeys" | "copyCredentialCallback" > & {
  createApiKeyFunction: CreateApiKeyFunction,
  deleteApiKeyFunction: DeleteApiKeyFunction,
  allowEditing?: boolean;
}

type ApiKeyListRowProps = {
  apiKey: string;
  deleteStatus: ApiKeyRequestStatus;
  isDeleteDisabled: boolean;
  onDeleteApiKey: (apikey: string) => Promise<void>;
  copyCredentialCallback: Function;
  errorMessage?: string;
}

const ApiKeyListRow: React.FC<ApiKeyListRowProps> = ({apiKey, copyCredentialCallback, deleteStatus, isDeleteDisabled, onDeleteApiKey, errorMessage}) => {
  return <>
    <div className="setup-tab__connection__creds__cred__item_row__content">
      <CopyBox copyCallback={() => copyCredentialCallback()} value={apiKey} />                         
      {deleteStatus === "error" && <InlineNotification duration={5000} status="error" autoDisappear={true}>{errorMessage || "Failed to delete. Please try again"}</InlineNotification>}
    </div>
    <div className="setup-tab__connection__creds__cred__delete">
      <DeleteApiKeyButton isDisabled={ isDeleteDisabled } status={deleteStatus} onClick={() => {
        return onDeleteApiKey(apiKey);
      }}/>
    </div>
  </>
}

let pendingOperations: (() => void)[] = [];

export const ApiKeyList: React.FC<ApiKeyListProps> = ({apiKeys, copyCredentialCallback, createApiKeyFunction, deleteApiKeyFunction, allowEditing }) => {
  const [createApiKeyStatus, setCreateApiKeyStatus] = useState<ApiKeyRequestStatus>()
  const [mapDeleteApiKeyStatus, setMapDeleteApiKeyStatus] = useState<Record<string, ApiKeyRequestStatus>>({})
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const onCreateApiKey = async () => {
    setCreateApiKeyStatus("loading")
    setErrorMessage(undefined);

    if (!allowEditing) {
      setErrorMessage("Editing is disabled for this app because it is undergoing maintainence.")
      setCreateApiKeyStatus("error");
      setTimeout(() => {
        pendingOperations.push(() => {
          setCreateApiKeyStatus(undefined);
        })
      }, 4000)
      return;
    }

    const result = await createApiKeyFunction().catch(() => {
      setCreateApiKeyStatus("error")
    })
    if (result !== undefined) { 
      setCreateApiKeyStatus("success")
      pendingOperations.push(() => {
        setCreateApiKeyStatus(undefined);
      })
    }
  }  
  
  const onDeleteApiKey = async (apikey: string) => {
    if (!allowEditing) {
      setMapDeleteApiKeyStatus(state => ({ ...state, [apikey]: "error"}))
      setErrorMessage("Editing is disabled for this app because it is undergoing maintainence.")
      setTimeout(() => {
        pendingOperations.push(() => {
          let currentMap = mapDeleteApiKeyStatus;
          mapDeleteApiKeyStatus[apikey] = undefined;
          setMapDeleteApiKeyStatus({...currentMap});
        });
      }, 4000)
      return;
    }

    setErrorMessage(undefined);
    setMapDeleteApiKeyStatus(state => ({ ...state, [apikey]: "loading"}))
    const result = await deleteApiKeyFunction(apikey).catch(() => setMapDeleteApiKeyStatus(state => ({ ...state, [apikey]: "error"})))
    if (result !== undefined) { 
      setMapDeleteApiKeyStatus(state => ({ ...state, [apikey]: "success"})) 
      pendingOperations.push(() => {
        let currentMap = mapDeleteApiKeyStatus;
        mapDeleteApiKeyStatus[apikey] = undefined;
        setMapDeleteApiKeyStatus({...currentMap});
      });
    }
  }  
  
  const isDeleteDisabled = apiKeys.filter(apiKey => mapDeleteApiKeyStatus[apiKey] == undefined || mapDeleteApiKeyStatus[apiKey] === "error").length < 2;

  const performPendingOperations = () => {
    for (let i = 0; i < pendingOperations.length; i++) {
      pendingOperations[i]();
    }
    pendingOperations = [];
  }

  useEffect(() => {
    document.addEventListener(CustomEventNames.dashboard.onAppDataFetched, performPendingOperations);

    return () => {
      document.removeEventListener(CustomEventNames.dashboard.onAppDataFetched, performPendingOperations);
    }
  }, [])

  return <>
    <div className="setup-tab__connection__creds__cred__value">
      <div className="setup-tab__connection__creds__cred__items">
        { apiKeys.map((apiKey) => {
            const deleteStatus = mapDeleteApiKeyStatus[apiKey];
            return deleteStatus === "success" ? 
              <TemporaryContent autoDismiss={false} key={apiKey}>
                <div className="setup-tab__connection__creds__cred__item_row">
                  <InlineNotification status="success" icon="static/assets/dashboard/trash-green.png">API key deleted successfully</InlineNotification>
                </div>
              </TemporaryContent>:
              <div className="setup-tab__connection__creds__cred__item_row" key={apiKey}>
                  <ApiKeyListRow apiKey={apiKey} copyCredentialCallback={copyCredentialCallback} 
                    deleteStatus={deleteStatus} isDeleteDisabled={isDeleteDisabled}
                    onDeleteApiKey={onDeleteApiKey} errorMessage={errorMessage}/>
              </div>;            
        })}   
      </div>
      <div className="setup-tab__connection__creds__cred__border"></div>
      <div className="setup-tab__connection__creds__cred__create">
        <CreateApiKeyButton errorMessage={errorMessage} status={createApiKeyStatus} onClick={onCreateApiKey}></CreateApiKeyButton>
      </div>
    </div>  
  </>
};

export default ApiKeyList;
